/**
 * Order alerts / PWA help for delivery staff (local.gosnel.com).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Container,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import HomeIcon from '@mui/icons-material/Home';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeliveryStaffShell from '../components/DeliveryStaffShell';
import { getStaffSession, staffAppPath } from '../lib/deliveryStaffApi';

const LANG_KEY = 'staff_notif_lang_v1';
const ENABLED_KEY = 'staff_notif_enabled_v1';

const LANGS = [
  { id: 'en', label: 'EN' },
  { id: 'ar', label: 'عربي' },
  { id: 'ur', label: 'اردو' },
];

const COPY = {
  en: {
    title: 'Order alerts',
    subtitle:
      'Get a chime (and optional browser notification) when a resident places an order. Add local.gosnel.com to Home Screen so alerts work with the phone locked.',
    setupTitle: 'Setup steps',
    steps: [
      {
        primary: '1. Add local.gosnel.com to Home Screen',
        secondary: (
          <>
            <strong>iPhone / iPad:</strong> Safari → Share → Add to Home Screen.
            <br />
            <strong>Android:</strong> Chrome menu → Install app / Add to Home screen.
          </>
        ),
      },
      {
        primary: '2. Open from the Home Screen icon',
        secondary:
          'Launch Local Partners from the icon (not a random Safari/Chrome tab), then sign in with your staff username.',
      },
      {
        primary: '3. Enable browser notifications',
        secondary:
          'Tap the button below. If blocked, keep the Delivery Orders page open — new orders still play a chime when you refresh.',
      },
      {
        primary: '4. Keep Delivery Orders open for the chime',
        secondary:
          'Even without push, leaving Orders open (or returning often) plays an alert when a new order arrives.',
      },
    ],
    permTitle: 'Browser permission',
    statusLabel: 'Status',
    statusUnsupported: 'not supported',
    enableBtn: 'Enable browser notifications',
    enabledBtn: 'Notifications enabled',
    openOrders: 'Open Delivery Orders',
    msgUnsupported:
      'This browser does not support notifications. Keep the Orders page open for chimes.',
    msgGranted:
      'Notifications enabled. You can also keep Orders open for the chime.',
    msgDenied:
      'Permission denied. Use browser site settings to allow notifications, or keep Orders open for the sound chime.',
    msgDefault:
      'Permission not granted yet. Try again from the Home Screen app icon if prompted.',
    msgError: 'Could not request notification permission.',
    previewBody: 'Delivery order alerts are on for this device.',
  },
  ar: {
    title: 'تنبيهات الطلبات',
    subtitle:
      'احصل على صوت تنبيه (وإشعار متصفح اختياري) عند طلب جديد. أضف local.gosnel.com إلى الشاشة الرئيسية لتعمل التنبيهات والقفل مغلق.',
    setupTitle: 'خطوات الإعداد',
    steps: [
      {
        primary: '1. أضف local.gosnel.com إلى الشاشة الرئيسية',
        secondary: (
          <>
            <strong>آيفون / آيباد:</strong> Safari ← مشاركة ← إضافة إلى الشاشة الرئيسية.
            <br />
            <strong>أندرويد:</strong> قائمة Chrome ← تثبيت التطبيق / إضافة إلى الشاشة الرئيسية.
          </>
        ),
      },
      {
        primary: '2. افتح من أيقونة الشاشة الرئيسية',
        secondary:
          'شغّل Local Partners من الأيقونة، ثم سجّل الدخول بحساب الموظف.',
      },
      {
        primary: '3. فعّل إشعارات المتصفح',
        secondary:
          'اضغط الزر أدناه. إذا تم الحظر، اترك صفحة طلبات التوصيل مفتوحة.',
      },
      {
        primary: '4. أبقِ طلبات التوصيل مفتوحة لصوت التنبيه',
        secondary:
          'حتى بدون إشعارات دفع، ترك الصفحة مفتوحة يشغّل تنبيهاً عند وصول طلب جديد.',
      },
    ],
    permTitle: 'إذن المتصفح',
    statusLabel: 'الحالة',
    statusUnsupported: 'غير مدعوم',
    enableBtn: 'تفعيل إشعارات المتصفح',
    enabledBtn: 'الإشعارات مفعّلة',
    openOrders: 'فتح طلبات التوصيل',
    msgUnsupported:
      'هذا المتصفح لا يدعم الإشعارات. أبقِ صفحة الطلبات مفتوحة لسماع التنبيه.',
    msgGranted: 'تم تفعيل الإشعارات. يمكنك أيضاً إبقاء الطلبات مفتوحة لصوت التنبيه.',
    msgDenied:
      'تم رفض الإذن. اسمح بالإشعارات من إعدادات الموقع، أو أبقِ الطلبات مفتوحة.',
    msgDefault: 'لم يُمنح الإذن بعد. حاول مرة أخرى من أيقونة التطبيق على الشاشة الرئيسية.',
    msgError: 'تعذّر طلب إذن الإشعارات.',
    previewBody: 'تنبيهات توصيل الطلبات مفعّلة على هذا الجهاز.',
  },
  ur: {
    title: 'آرڈر الرٹس',
    subtitle:
      'نئے آرڈر پر چائم (اور اختیاری براؤزر نوٹیفکیشن) حاصل کریں۔ فون لاک ہونے پر الرٹس کے لیے local.gosnel.com ہوم اسکرین پر شامل کریں۔',
    setupTitle: 'سیٹ اپ مراحل',
    steps: [
      {
        primary: '1. local.gosnel.com کو ہوم اسکرین پر شامل کریں',
        secondary: (
          <>
            <strong>آئی فون / آئی پیڈ:</strong> Safari → Share → Add to Home Screen۔
            <br />
            <strong>اینڈرائیڈ:</strong> Chrome مینو → Install app / Add to Home screen۔
          </>
        ),
      },
      {
        primary: '2. ہوم اسکرین آئیکن سے کھولیں',
        secondary:
          'Local Partners کو آئیکن سے کھولیں، پھر اپنے سٹاف یوزرنیم سے سائن ان کریں۔',
      },
      {
        primary: '3. براؤزر نوٹیفکیشنز آن کریں',
        secondary:
          'نیچے والے بٹن پر دبائیں۔ اگر بلاک ہو تو Delivery Orders کھلا رکھیں۔',
      },
      {
        primary: '4. چائم کے لیے Delivery Orders کھلا رکھیں',
        secondary:
          'بغیر پش کے بھی، Orders کھلا رکھنے سے نیا آرڈر آنے پر الرٹ چلتا ہے۔',
      },
    ],
    permTitle: 'براؤزر اجازت',
    statusLabel: 'حیثیت',
    statusUnsupported: 'سپورٹ نہیں',
    enableBtn: 'براؤزر نوٹیفکیشنز آن کریں',
    enabledBtn: 'نوٹیفکیشنز آن ہیں',
    openOrders: 'Delivery Orders کھولیں',
    msgUnsupported:
      'یہ براؤزر نوٹیفکیشنز سپورٹ نہیں کرتا۔ چائم کے لیے Orders صفحہ کھلا رکھیں۔',
    msgGranted: 'نوٹیفکیشنز آن ہیں۔ چائم کے لیے Orders بھی کھلا رکھ سکتے ہیں۔',
    msgDenied:
      'اجازت مسترد۔ براؤزر سائٹ سیٹنگز سے اجازت دیں، یا Orders کھلا رکھیں۔',
    msgDefault: 'ابھی اجازت نہیں ملی۔ ہوم اسکرین ایپ آئیکن سے دوبارہ کوشش کریں۔',
    msgError: 'نوٹیفکیشن اجازت کی درخواست ناکام۔',
    previewBody: 'اس ڈیوائس پر ڈیلیوری آرڈر الرٹس آن ہیں۔',
  },
};

const STEP_ICONS = [HomeIcon, OpenInNewIcon, NotificationsActiveIcon, VolumeUpIcon];

function readLang() {
  try {
    const v = localStorage.getItem(LANG_KEY);
    if (v === 'ar' || v === 'ur' || v === 'en') return v;
  } catch {
    /* ignore */
  }
  return 'en';
}

function writeEnabledSoft(on) {
  try {
    if (on) localStorage.setItem(ENABLED_KEY, '1');
    else localStorage.removeItem(ENABLED_KEY);
  } catch {
    /* ignore */
  }
}

export default function DeliveryStaffNotificationsHelp() {
  const navigate = useNavigate();
  const [lang, setLang] = useState(readLang);
  const [perm, setPerm] = useState(() => {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission;
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const t = COPY[lang] || COPY.en;
  const rtl = lang === 'ar' || lang === 'ur';
  const showEnabled = perm === 'granted';
  const session = getStaffSession();

  useEffect(() => {
    if (perm === 'granted') writeEnabledSoft(true);
  }, [perm]);

  useEffect(() => {
    try {
      localStorage.setItem('delivery_staff_last_role', 'staff');
    } catch {
      /* ignore */
    }
  }, []);

  const onLangChange = (_e, next) => {
    setLang(next);
    try {
      localStorage.setItem(LANG_KEY, next);
    } catch {
      /* ignore */
    }
  };

  const requestPermission = async () => {
    setMsg('');
    if (typeof Notification === 'undefined') {
      setMsg(t.msgUnsupported);
      return;
    }
    setBusy(true);
    try {
      const { subscribeStaffPush, isIosDevice, isStandalonePwa } = await import(
        '../lib/staffPush'
      );
      if (isIosDevice() && !isStandalonePwa()) {
        setMsg(t.msgDefault);
        setBusy(false);
        return;
      }
      const ok = await subscribeStaffPush();
      const result =
        typeof Notification !== 'undefined'
          ? Notification.permission
          : 'unsupported';
      setPerm(result);
      if (ok || result === 'granted') {
        writeEnabledSoft(true);
        setMsg(t.msgGranted);
        try {
          new Notification('GoSnel Delivery', {
            body: t.previewBody,
            icon: '/icon-192.png',
          });
        } catch {
          /* ignore */
        }
      } else if (result === 'denied') {
        writeEnabledSoft(false);
        setMsg(t.msgDenied);
      } else {
        setMsg(t.msgDefault);
      }
    } catch (err) {
      setMsg(err?.message || t.msgError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <DeliveryStaffShell title="Order alerts">
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Typography
          variant="h5"
          fontWeight={900}
          sx={{ mb: 0.5 }}
          dir={rtl ? 'rtl' : 'ltr'}
        >
          {t.title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1.5 }}
          dir={rtl ? 'rtl' : 'ltr'}
        >
          {t.subtitle}
          {session.displayName ? ` (${session.displayName})` : ''}
        </Typography>

        <Tabs
          value={lang}
          onChange={onLangChange}
          sx={{
            mb: 2,
            minHeight: 40,
            '& .MuiTab-root': { minHeight: 40, fontWeight: 800 },
          }}
        >
          {LANGS.map((l) => (
            <Tab key={l.id} value={l.id} label={l.label} />
          ))}
        </Tabs>

        {msg && (
          <Alert
            severity={perm === 'granted' ? 'success' : 'info'}
            sx={{ mb: 2, borderRadius: 2 }}
            onClose={() => setMsg('')}
          >
            {msg}
          </Alert>
        )}

        <Paper
          variant="outlined"
          sx={{ p: 2.5, borderRadius: 2, mb: 2 }}
          dir={rtl ? 'rtl' : 'ltr'}
        >
          <Typography fontWeight={800} sx={{ mb: 1 }}>
            {t.setupTitle}
          </Typography>
          <List dense disablePadding>
            {t.steps.map((step, i) => {
              const Icon = STEP_ICONS[i];
              return (
                <ListItem key={i} alignItems="flex-start" sx={{ px: 0 }}>
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      mt: 0.5,
                      ...(rtl ? { mr: 0, ml: 1 } : {}),
                    }}
                  >
                    <Icon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={step.primary}
                    secondary={step.secondary}
                    primaryTypographyProps={{ fontWeight: 800 }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }} dir={rtl ? 'rtl' : 'ltr'}>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              {showEnabled ? (
                <CheckCircleIcon color="success" />
              ) : (
                <NotificationsActiveIcon color="primary" />
              )}
              <Box>
                <Typography fontWeight={800}>{t.permTitle}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t.statusLabel}:{' '}
                  <strong>
                    {perm === 'unsupported'
                      ? t.statusUnsupported
                      : showEnabled
                        ? 'granted'
                        : perm || 'default'}
                  </strong>
                </Typography>
              </Box>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="contained"
                startIcon={<NotificationsActiveIcon />}
                onClick={requestPermission}
                disabled={busy || showEnabled || perm === 'unsupported'}
                sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d9488' } }}
              >
                {showEnabled ? t.enabledBtn : t.enableBtn}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate(staffAppPath('/delivery-staff/orders'))}
              >
                {t.openOrders}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Container>
    </DeliveryStaffShell>
  );
}
